import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  User,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  MapPin,
  Eye,
  FileText,
  Calendar,
  XCircle,
  CheckCircle,
  Clock,
  Package,
} from 'lucide-react';
import { Dispute } from '../../types/dispute';
import { useDisputesStore } from '../../stores/disputesStore';
import { EvidenceViewModal } from './evidence/EvidenceViewModal';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  dispute: Dispute;
};

export const DisputeCard = ({ dispute }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedGuideId, setSelectedGuideId] = useState<string | null>(
    dispute.resolvedGuideId || null
  );
  const [viewingEvidence, setViewingEvidence] = useState<{
    guideName: string;
    evidence?: Evidence;
  } | null>(null);
  const { resolveDispute } = useDisputesStore();
  const { translations } = useLanguage();

  const handleResolve = async () => {
    if (!selectedGuideId) {
      toast.error(translations?.disputeCard?.selectGuide || 'You must select a guide');
      return;
    }

    try {
      await resolveDispute(dispute.id, selectedGuideId);
      toast.success(translations?.disputeCard?.resolveSuccess || 'Dispute resolved successfully');
    } catch (error: any) {
      console.error('Error resolving dispute:', error);
      toast.error(translations?.disputeCard?.resolveError || 'Failed to resolve dispute');
    }
  };

  const handleEmailClick = () => {
    if (!dispute.pilgrimEmail) {
      toast.error(translations?.disputeCard?.emailNotAvailable || 'Email not available');
      return;
    }

    const guideNames = dispute.disputingGuides
      .map(g => g.guideName)
      .join(' & ');
    
    const subject = encodeURIComponent(
      translations?.disputeCard?.emailSubject || 'Important: Guide Verification Required - Ithraa Al Khair Technical Support'
    );
    
    const body = encodeURIComponent(
      `${translations?.disputeCard?.emailBodyPart1 || 'Dear'} ${dispute.pilgrimName},\n\n` +
        `${translations?.disputeCard?.emailBodyPart2 || 'We are contacting you from Ithraa Al Khair Technical Support regarding your Hajj arrangements.'}\n\n` +
        `${translations?.disputeCard?.emailBodyPart3 || 'We have received conflicting information about your assigned guide. According to our records, both'} ${guideNames} ${translations?.disputeCard?.emailBodyPart4 || 'claim to be your official guide.'}\n\n` +
        `${translations?.disputeCard?.emailBodyPart5 || 'Could you please confirm which one is your actual assigned guide? This information is crucial for ensuring proper service delivery during your Hajj journey.'}\n\n` +
        `${translations?.disputeCard?.emailBodyPart6 || 'Best regards,'}\n` +
        `${translations?.disputeCard?.emailBodyPart7 || 'Ithraa Al Khair Technical Support Team'}`
    );

    window.location.href = `mailto:${dispute.pilgrimEmail}?subject=${subject}&body=${body}`;
  };

  const handleWhatsAppClick = () => {
    const guideNames = dispute.disputingGuides
      .map((g) => g.guideName)
      .join(' & ');

    const message = encodeURIComponent(
      `${translations?.disputeCard?.whatsAppMessage1 || 'Dear'} ${dispute.pilgrimName},\n\n` +
        `${translations?.disputeCard?.whatsAppMessage2 || 'This is Ithraa Al Khair Technical Support team contacting you regarding your Hajj arrangements.'}\n\n` +
        `${translations?.disputeCard?.whatsAppMessage3 || 'We have received conflicting information about your assigned guide. According to our records, both'} ${guideNames} ${translations?.disputeCard?.whatsAppMessage4 || 'claim to be your official guide.'}\n\n` +
        `${translations?.disputeCard?.whatsAppMessage5 || 'Could you please confirm which one is your actual assigned guide? This information is crucial for ensuring proper service delivery during your Hajj journey.'}\n\n` +
        `${translations?.disputeCard?.whatsAppMessage6 || 'Best regards,'}\n` +
        `${translations?.disputeCard?.whatsAppMessage7 || 'Ithraa Al Khair Technical Support Team'}`
    );

    if (dispute.pilgrimPhone) {
      const formattedPhone = dispute.pilgrimPhone.replace(/\D/g, '');
      window.open(`https://wa.me/${formattedPhone}?text=${message}`, '_blank');
    } else {
      toast.error(translations?.disputeCard?.phoneNotAvailable || 'Phone number not available');
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-red-50 rounded-lg">
              <AlertTriangle className="text-red-500" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold">{dispute.pilgrimName}</h3>
              <div className="text-sm text-gray-500">
                {translations?.disputeCard?.passport || 'Passport Number'}: {dispute.passportNumber}
              </div>
              <div className="text-sm text-gray-500">
                {translations?.disputeCard?.country || 'Country'}: {dispute.pilgrimCountry || 'Not specified'}
              </div>
              {dispute.packageName && (
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <Package size={14} className="text-gray-400" />
                  <span>{dispute.packageName}</span>
                </div>
              )}
              <div className="text-sm text-gray-500">
                {translations?.disputeCard?.createdAt || 'Created'}: {formatDate(dispute.createdAt)}
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            {isExpanded ? (
              <ChevronUp className="text-gray-500" />
            ) : (
              <ChevronDown className="text-gray-500" />
            )}
          </button>
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Mail className="text-gray-400" size={20} />
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                {dispute.pilgrimEmail || translations?.disputeCard?.emailNotAvailable || 'Not available'}
              </p>
              {dispute.pilgrimEmail && (
                <button
                  onClick={handleEmailClick}
                  className="text-primary-500 hover:text-primary-600 text-sm"
                >
                  {translations?.disputeCard?.sendEmail || 'Send Email'}
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="text-gray-400" size={20} />
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                {dispute.pilgrimPhone || translations?.disputeCard?.phoneNotAvailable || 'Not available'}
              </p>
              {dispute.pilgrimPhone && (
                <button
                  onClick={handleWhatsAppClick}
                  className="text-green-500 hover:text-green-600 text-sm"
                >
                  {translations?.disputeCard?.sendWhatsApp || 'Send WhatsApp'}
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-sm text-gray-600">
              {dispute.pilgrimCountry || translations?.disputeCard?.countryNotAvailable || 'Not specified'}
            </div>
          </div>
        </div>

        {/* Guides List */}
        <div className="space-y-6">
          {dispute.disputingGuides.map((guide) => (
            <div key={guide.guideId} className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-50 rounded-full">
                    <User className="text-primary-500" size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium">{guide.guideName}</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      {translations?.disputeCard?.organizer || 'Organizer'}: {guide.organizerName || 'Not specified'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {translations?.disputeCard?.requestDate || 'Request Date'}: {formatDate(guide.submittedAt)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <button
                      onClick={() => setViewingEvidence({
                        guideName: guide.guideName,
                        evidence: guide.evidence,
                      })}
                      className="flex items-center gap-2 text-primary-500 hover:text-primary-600"
                    >
                      <Eye size={18} />
                      <span>{translations?.disputeCard?.viewEvidence || 'View Evidence'}</span>
                      {guide.evidence && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                      )}
                    </button>
                  </div>
                  <input
                    type="radio"
                    name={`dispute-${dispute.id}`}
                    checked={selectedGuideId === guide.guideId}
                    onChange={() => setSelectedGuideId(guide.guideId)}
                    className="w-5 h-5 text-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Resolution Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleResolve}
            disabled={!selectedGuideId}
            className={`px-6 py-2 rounded-lg text-white ${
              selectedGuideId
                ? 'bg-primary-500 hover:bg-primary-600'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {translations?.disputeCard?.resolveDispute || 'Resolve Dispute'} 
          </button>
        </div>
      </motion.div>

      {/* Evidence Modal */}
      {viewingEvidence && (
        <EvidenceViewModal
          isOpen={true}
          onClose={() => setViewingEvidence(null)}
          guideName={viewingEvidence.guideName}
          evidence={viewingEvidence.evidence}
        />
      )}
    </>
  );
};